import { Alert, Spin, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { publicWorkflowsApi } from '@/api/publicWorkflows';
import { type WorkflowTemplate } from '@/api/workflows';
import { useSetSmartFormPlatform } from '@/components/layout/SmartFormContext';
import { getRuntimeConfig, publicFormLandingUrl } from '@/config/runtimeConfig';
import { FormShell } from '@/form-renderer/FormShell';
import { useFormBranch } from '@/form-renderer/useFormBranch';

const { Text } = Typography;

/** Rejects a path param that cannot be a template id before it costs a request. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The Welcome step's CANCEL, as it behaves with no BMO page configured for the customer's
 * language — and, since nothing is persisted here, never a write path either way.
 *
 * Going back in the browser was the ONLY exit before `publicFormLandingUrl` existed: a customer
 * arrived from a link on somebody else's page, so that page was the single destination we knew
 * of. It stays the fallback rather than becoming dead code, because the config key ships empty
 * until each environment is given a URL and because a customer who reached the form some other
 * way is still better served by the page they came from.
 *
 * `history.length > 1` is the only signal a browser gives for "there is an entry to go
 * back to". A form opened in a fresh tab (a `target="_blank"` link, or a pasted URL) has
 * none and `back()` would be a silent no-op, so fall back to the referring page when the
 * browser named one. With neither there is genuinely nowhere to go — the button still
 * shows, because it works for how customers actually arrive.
 *
 * Exported for the test — the fallback chain is the part worth pinning, and a stubbed
 * `window.location` cannot be driven through a rendered tree without the page's own
 * effects reading the stub.
 */
export function leaveToPreviousPage(): void {
  if (window.history.length > 1) {
    window.history.back();
    return;
  }
  if (document.referrer) window.location.assign(document.referrer);
}

/**
 * The Welcome step's CANCEL in full: BMO's Applications & Forms page for the language the
 * customer is reading in, or — with none configured for it — the browser-history exit above.
 *
 * `language` is the PAGE's locale, the one the Welcome card's copy is being read in, never the
 * language chosen for the generated DOCUMENTS. The two diverge the moment that step's radio is
 * touched, and the destination here is a BMO web page, not a document.
 *
 * A free function taking the language rather than a closure inside the page, for the same reason
 * `leaveToPreviousPage` is one: a stubbed `window.location` cannot be driven through a rendered
 * tree without this page's own effects reading the stub, so the destination is pinned by calling
 * it directly. Nothing is saved on the way out — there is no write path on this page to save with.
 */
export function leaveToFormsPage(language: 'en' | 'fr'): void {
  const landing = publicFormLandingUrl(getRuntimeConfig(), language);
  if (landing) {
    window.location.assign(landing);
    return;
  }
  leaveToPreviousPage();
}

/**
 * The PUBLIC customer runtime — `/ext/workflows/{workflowId}/new`.
 *
 * A person with no BMO login fills the workflow, generates their PDFs, downloads them and
 * carries the printed forms to a branch. Spec §2 ("Public user") and §12.
 *
 * <h3>What this page does NOT do, and why the absence is the design</h3>
 * Every write handler `FormShell` accepts is deliberately withheld: no `onSave`, `onSubmit`,
 * `onSaveAndExit`, `applicationId`, `bmoStatus`, `creationTimestamp`, `initialValues`,
 * `isResumedApplication` or `onOptionRedirect`. That is the same "absence rather than a flag"
 * guard `onSave`'s own doc comment describes — with no handler there is no BMO write path, no
 * FileX path and no navigation target to reach, rather than one that is merely disabled.
 *
 * It also never imports `@/api/applications` or `@/api/workflows`' client methods. An ESLint
 * `no-restricted-imports` rule on `src/public/**` makes reaching for them a build failure, so
 * "the customer page cannot save" is enforced rather than remembered.
 *
 * Form BRANCHING is wired, and is not an exception to any of the above. A branch loads a
 * second published workflow and swaps the steps below the rule — one more anonymous GET
 * through `publicWorkflowsApi`, no session, no write, nothing persisted. `useFormBranch`
 * takes its loader as an argument precisely so this page can hand it the public client and
 * keep the import ban intact; see the hook's own doc comment.
 *
 * <h3>Consequences worth knowing before editing</h3>
 * Nothing is persisted anywhere — not to BMO, not to our storage, not to localStorage. A
 * refresh loses the entire form, which is why the notice below exists. The customer's answers
 * live in exactly two places: this tab's React memory, and the body of the PDF-generation
 * request. That is the cleanest PII posture on the platform (spec §14).
 *
 * This page sits OUTSIDE `AuthGuard`, so it gets no `useSessionTimer` and no `useTabSlot`:
 * there is no session to refresh and no tab budget to enforce. Both absences are correct —
 * please do not "fix" the missing tab gate.
 */
export function PublicWorkflowPage() {
  const { t, i18n } = useTranslation();
  const { workflowId } = useParams<{ workflowId: string }>();
  // Drives every downstream string — field labels, rail titles, the ThankYou copy. A
  // hardcoded 'en' would leave a French customer reading English until the Welcome step's
  // language radio fires, and workflows without one would never recover.
  const language: 'en' | 'fr' = i18n.language.startsWith('fr') ? 'fr' : 'en';

  const [template, setTemplate] = useState<WorkflowTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Tells the header which back office this workflow belongs to, which decides the support
  // number shown in the contact block — IFDS and APEX differ, and for IFDS the French line is
  // a different number, not a translation. Cleared on unmount so a stale platform cannot
  // outlive this page.
  // A branching answer swaps the steps below the rule for another published workflow's. The
  // loader is the ANONYMOUS client — `/public/workflows/{id}` serves only what is currently
  // published, so a customer can only ever be branched onto a form that is live, and no
  // session is involved at either end.
  const {
    effectiveTemplate,
    formBranch,
    onFormBranchChange,
    loading: branchLoading,
    loadFailed: branchLoadFailed,
  } = useFormBranch({ template, loadTemplate: publicWorkflowsApi.get });

  const setHeaderPlatform = useSetSmartFormPlatform();
  useEffect(() => {
    setHeaderPlatform((template?.platform as 'IFDS' | 'APEX' | undefined) ?? null);
    return () => setHeaderPlatform(null);
  }, [template?.platform, setHeaderPlatform]);

  useEffect(() => {
    // A malformed id is answered locally. The endpoint would 404 anyway, but spending a
    // request on a value that cannot possibly be a template id is pure cost on a route
    // whose whole premise is that it makes almost none.
    if (!workflowId || !UUID.test(workflowId)) {
      setLoadError(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    void (async () => {
      try {
        const fresh = await publicWorkflowsApi.get(workflowId);
        if (!cancelled) setTemplate(fresh);
      } catch {
        // One message for every failure, deliberately. The server answers 404 for seven
        // distinct conditions — unknown, deleted, archived, unpublished, never published, no
        // approved version — and telling a stranger which would be an existence leak. There
        // is also nothing a customer could do differently with the detail.
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workflowId]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }

  if (loadError || !template) {
    // No retry button and no link out: there is no dashboard to return to, and a reload
    // cannot fix an unpublished workflow. The customer needs to go back to whoever gave
    // them the link.
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" showIcon message={t('public.workflow.unavailable')} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{ padding: '16px 0 8px' }}>
        <Text strong style={{ fontSize: 18, color: '#0f172a' }}>
          {(language === 'fr' ? template.nameFr : template.nameEn) ||
            template.nameEn ||
            template.appName}
        </Text>
      </div>
      {/* The one thing a customer cannot discover for themselves. Nothing is saved on
          either side, so a refresh, a crashed tab or a backgrounded mobile browser loses
          everything — and unlike the advisor flow there is no draft to come back to. */}
      <Alert
        type="info"
        showIcon
        closable
        style={{ marginBottom: 12 }}
        message={t('public.workflow.notSavedNotice')}
      />
      {/* Fetching the form the customer's answer just selected. Rendered ABOVE the shell
          rather than in place of it: swapping in the page-level <Spin /> would unmount
          FormShell and take every answer with it — and here there is no draft to come back
          to. */}
      {branchLoading && (
        <Alert
          type="info"
          showIcon
          icon={<Spin size="small" />}
          style={{ marginBottom: 12 }}
          message={t('public.workflow.branchLoading')}
        />
      )}
      {/* The selected form is not currently published, so we stayed on this one. Phrased for
          a customer, who can do nothing about it and should not be told why — the same
          no-existence-leak reasoning as the page-level error above. */}
      {branchLoadFailed && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('public.workflow.branchUnavailable')}
        />
      )}
      <FormShell
        // The COMPOSED template once a branch is active — never a different `key`, which
        // would discard every answer including the one that selected this form.
        template={effectiveTemplate ?? template}
        formBranch={formBranch}
        onFormBranchChange={onFormBranchChange}
        language={language}
        mode="public"
        generatePdf={publicWorkflowsApi.generatePdfPreview}
        // Every Canada Post Find is a billed transaction fired on keystroke, and the proxy
        // is role-gated so an anonymous caller would get a dead dropdown anyway. The
        // customer types their address into a plain input.
        addressAutocomplete={false}
        // Same bargain for ZeroBounce: every verify is a billed transaction, and
        // /integrations/email/verify is role-gated on purpose (AnonymousSurfaceTests pins it
        // beside Canada Post), so a customer's every blur would buy nothing but a console
        // breadcrumb. The local RFC-5322 rule still runs and still names a malformed address.
        emailVerification={false}
        // The ONE handler this page wires, and deliberately not a write path: CANCEL on the
        // Welcome step leaves for BMO's Applications & Forms page — the list the customer
        // picked this form from. Without a handler at all WelcomeStep hid the button entirely
        // (it keys off the handler's presence), leaving a customer who opened the wrong form no
        // way out but the browser chrome.
        //
        // With no configured page for the active locale it keeps the browser-history exit; see
        // `leaveToFormsPage`.
        onWelcomeCancel={() => leaveToFormsPage(language)}
        // No onSave / onSubmit / onSaveAndExit / applicationId / bmoStatus /
        // creationTimestamp / initialValues / isResumedApplication / onOptionRedirect.
        // See the class comment: the absence IS the guard.
      />
    </div>
  );
}
